import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Section } from '../types/models';
import { useAppContext } from '../../revision-system/context/RevisionContext';
import './SectionList.css';

type DeleteOption = 'cancel' | 'delete-cards' | 'move-to-default';

interface SectionListProps {
  sections: Section[];
  onSectionsChange: () => Promise<void>;
}

/**
 * SectionList subcomponent
 * Displays all sections with CRUD operations
 * Requirements: 2.1, 2.2, 2.3, 2.5
 */
const SectionList: React.FC<SectionListProps> = ({ sections, onSectionsChange }) => {
  const navigate = useNavigate();
  const { user, config, firestoreService } = useAppContext();

  // Create section state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newSectionName, setNewSectionName] = useState('');
  const [createError, setCreateError] = useState('');

  // Rename section state
  const [renamingSectionId, setRenamingSectionId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameError, setRenameError] = useState('');

  // Delete section state
  const [deletingSectionId, setDeletingSectionId] = useState<string | null>(null);
  const [deleteOption, setDeleteOption] = useState<DeleteOption>('cancel');

  /**
   * Handle create section
   */
  const handleCreateSection = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateError('');

    if (!newSectionName.trim()) {
      setCreateError('Section name is required');
      return;
    }

    try {
      await firestoreService.createSection(newSectionName.trim());
      setNewSectionName('');
      setShowCreateForm(false);
      await onSectionsChange();
    } catch (error) {
      console.error('Error creating section:', error);
      setCreateError('Failed to create section. Please try again.');
    }
  };

  /**
   * Handle rename section
   */
  const handleRenameSection = async (sectionId: string) => {
    setRenameError('');

    if (!renameValue.trim()) {
      setRenameError('Section name is required');
      return;
    }

    try {
      await firestoreService.updateSection(sectionId, { name: renameValue.trim() });
      setRenamingSectionId(null);
      setRenameValue('');
      await onSectionsChange();
    } catch (error) {
      console.error('Error renaming section:', error);
      setRenameError('Failed to rename section. Please try again.');
    }
  };

  /**
   * Start rename process
   */
  const startRename = (section: Section) => {
    setRenamingSectionId(section.id);
    setRenameValue(section.name);
    setRenameError('');
  };

  /**
   * Cancel rename
   */
  const cancelRename = () => {
    setRenamingSectionId(null);
    setRenameValue('');
    setRenameError('');
  };

  /**
   * Handle delete section
   */
  const handleDeleteSection = async () => {
    if (!deletingSectionId || deleteOption === 'cancel') {
      setDeletingSectionId(null);
      return;
    }

    try {
      // Get cards in the section
      const cards = await firestoreService.getCardsBySection(deletingSectionId);

      if (deleteOption === 'delete-cards') {
        // Delete all cards in the section
        for (const card of cards) {
          await firestoreService.deleteCard(card.id);
        }
      } else if (deleteOption === 'move-to-default') {
        // Move cards to a default section
        // First, ensure a default section exists
        let defaultSection = sections.find(s => s.name === 'Default');
        if (!defaultSection) {
          defaultSection = await firestoreService.createSection('Default');
        }

        // Move all cards to default section
        for (const card of cards) {
          await firestoreService.updateCard(card.id, { sectionId: defaultSection.id });
        }
      }

      // Delete the section
      await firestoreService.deleteSection(deletingSectionId);
      setDeletingSectionId(null);
      setDeleteOption('cancel');
      await onSectionsChange();
    } catch (error) {
      console.error('Error deleting section:', error);
      alert('Failed to delete section. Please try again.');
    }
  };

  /**
   * Start delete process
   */
  const startDelete = (sectionId: string) => {
    setDeletingSectionId(sectionId);
    setDeleteOption('cancel');
  };

  /**
   * Cancel delete
   */
  const cancelDelete = () => {
    setDeletingSectionId(null);
    setDeleteOption('cancel');
  };

  return (
    <div className="section-list">
      <div className="section-list-header">
        <h2>All Sections</h2>
        <button
          className="btn btn-primary"
          onClick={() => setShowCreateForm(true)}
        >
          Create New Section
        </button>
      </div>

      {/* Create Section Form */}
      {showCreateForm && (
        <div className="create-section-form">
          <form onSubmit={handleCreateSection}>
            <div className="form-group">
              <label htmlFor="section-name">Section Name</label>
              <input
                id="section-name"
                type="text"
                value={newSectionName}
                onChange={(e) => setNewSectionName(e.target.value)}
                placeholder="Enter section name"
                autoFocus
              />
              {createError && <p className="error-message">{createError}</p>}
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                Create
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowCreateForm(false);
                  setNewSectionName('');
                  setCreateError('');
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Sections List */}
      {sections.length === 0 ? (
        <p className="empty-message">No sections yet. Create your first section!</p>
      ) : (
        <ul className="sections">
          {sections.map((section) => (
            <li key={section.id} className="section-item">
              {renamingSectionId === section.id ? (
                // Rename form
                <div className="rename-section-form">
                  <input
                    type="text"
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    autoFocus
                  />
                  {renameError && <p className="error-message">{renameError}</p>}
                  <div className="form-actions">
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => handleRenameSection(section.id)}
                    >
                      Save
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={cancelRename}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                // Normal display
                <>
                  <div className="section-info">
                    <h3>{section.name}</h3>
                    <p className="card-count">{section.cardCount} cards</p>
                  </div>
                  <div className="section-actions">
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => navigate(`${config.routing.basePath}/sections/${section.id}`)}
                    >
                      Manage
                    </button>
                    <button
                      className="btn btn-accent btn-sm"
                      onClick={() => navigate(`${config.routing.basePath}/session/${section.id}?mode=all`)}
                    >
                      Review All
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => startRename(section)}
                    >
                      Rename
                    </button>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => startDelete(section.id)}
                    >
                      Delete
                    </button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Delete Confirmation Dialog */}
      {deletingSectionId && (
        <div className="modal-overlay">
          <div className="modal delete-confirmation">
            <h3>Delete Section</h3>
            <p>
              This section contains{' '}
              {sections.find(s => s.id === deletingSectionId)?.cardCount || 0} cards.
              What would you like to do with them?
            </p>
            <div className="delete-options">
              <label>
                <input
                  type="radio"
                  name="delete-option"
                  value="delete-cards"
                  checked={deleteOption === 'delete-cards'}
                  onChange={(e) => setDeleteOption(e.target.value as DeleteOption)}
                />
                Delete all cards in this section
              </label>
              <label>
                <input
                  type="radio"
                  name="delete-option"
                  value="move-to-default"
                  checked={deleteOption === 'move-to-default'}
                  onChange={(e) => setDeleteOption(e.target.value as DeleteOption)}
                />
                Move cards to Default section
              </label>
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-danger"
                onClick={handleDeleteSection}
                disabled={deleteOption === 'cancel'}
              >
                Confirm Delete
              </button>
              <button
                className="btn btn-secondary"
                onClick={cancelDelete}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SectionList;
